import { Dialog, DialogContent, DialogTitle } from "@material-ui/core";
import React, { useRef, useState, useMemo, useEffect } from "react";
import { AiOutlineCloseCircle } from "react-icons/ai";
import ServiceCardThemeV2 from "../screens/FirstTimeProfile/ServiceCardThemeV2";
import CalendarVcardV2 from "../screens/FirstTimeProfile/CalendarVcardV2";
import { themesPallates } from "./Utils";

/* ─── helpers ─── */
const isPageDataReady = (data, themeVal) => {
  if (!data || typeof data !== "object") return false;
  if (themeVal === 1) {
    // Shape check only — onPreviewClick already fills placeholder values
    // ("Your Service Name" etc.) when the real fields are still empty.
    return !!(data?.eventDetails && data?.userDetails);
  }
  return !!(data?.response?.userDetails);
};

/* ─── modal ─── */
const ColorPickerModal = ({
  pageData,
  themeVal,
  open,
  onClose,
  onSelectColor,
  onNeedPreviewData,
  selectedPalette,
  currentProps,
}) => {
  const recaptcha = useRef();
  const ready = useMemo(() => isPageDataReady(pageData, themeVal), [pageData, themeVal]);

  useEffect(() => {
    if (open && onNeedPreviewData) {
      onNeedPreviewData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const previewData = useMemo(() => {
    if (!pageData) return null;
    const activeColors = selectedPalette || currentProps;

    return {
      ...pageData,
      primaryColor: activeColors.primary,
      secondaryColor: activeColors.secondary,
      headingColor: activeColors.heading,
      subHeadingColor: activeColors.subHeading,
      theme: activeColors,
      eventDetails: pageData.eventDetails
        ? {
            ...pageData.eventDetails,
            primaryColor: activeColors.primary,
            secondaryColor: activeColors.secondary,
            headingColor: activeColors.heading,
            subHeadingColor: activeColors.subHeading,
          }
        : pageData.eventDetails,
      result: pageData.result
        ? {
            ...pageData.result,
            primaryColor: activeColors.primary,
            secondaryColor: activeColors.secondary,
            headingColor: activeColors.heading,
            subHeadingColor: activeColors.subHeading,
          }
        : pageData.result,
    };
  }, [pageData, selectedPalette, currentProps]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle style={{ borderBottom: "1px solid #f0f0f0", padding: "14px 20px" }}>
        <span style={{ fontWeight: 700, fontSize: 16 }}>Choose a colour theme</span>
        <AiOutlineCloseCircle
          onClick={onClose}
          style={{ cursor: "pointer", float: "right", width: 22, height: 22, color: "#555", marginTop: 2 }}
        />
      </DialogTitle>

      <DialogContent style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", height: "82vh", overflow: "hidden" }}>

          {/* ── Left: palette list ── */}
          <div style={{
            width: 220, flexShrink: 0, borderRight: "1px solid #f0f0f0",
            overflowY: "auto", padding: "16px 14px", background: "#fafafa",
            display: "flex", flexDirection: "column", gap: 10,
          }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.7px", color: "#9ca3af", margin: "0 0 6px" }}>
              Palettes
            </p>
            {themesPallates.map((palette, index) => {
              const isActive = selectedPalette?.primary === palette.primary;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => onSelectColor(palette)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "9px 10px",
                    border: isActive ? "2px solid #a00030" : "2px solid transparent",
                    borderRadius: 10, background: isActive ? "#fff5f7" : "white",
                    cursor: "pointer", boxShadow: isActive ? "0 0 0 3px rgba(160,0,48,0.08)" : "0 1px 4px rgba(0,0,0,0.07)",
                    transition: "all 0.18s", width: "100%",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", width: 32, borderRadius: 7, overflow: "hidden", flexShrink: 0, border: "1px solid rgba(0,0,0,0.07)" }}>
                    {[palette.primary, palette.secondary, palette.heading, palette.subHeading].map((c, i) => (
                      <div key={i} style={{ background: c, height: 10 }} />
                    ))}
                  </div>
                  <div style={{ textAlign: "left", minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isActive ? "#a00030" : "#1a1d23", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {palette.name || `Theme ${index + 1}`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* ── Right: live preview ── */}
          <div style={{ flex: 1, overflowY: "auto", background: "#f4f5f7", position: "relative" }}>
            {ready ? (
              <>
                <div
                  style={{ position: "absolute", inset: 0, zIndex: 1, cursor: "default" }}
                  aria-hidden="true"
                />
                <div style={{ pointerEvents: "none", userSelect: "none" }}>
                  {themeVal === 1 ? (
                    <ServiceCardThemeV2
                      data={previewData}
                      selectService={() => {}}
                      userUpcomingMeting={() => {}}
                      onSendMsgClick={() => {}}
                      uploadDetails={() => {}}
                      updateState={() => {}}
                      confirmDetails={() => {}}
                      recaptcha={recaptcha}
                      viewAvailableSlot={() => {}}
                      isPreview
                    />
                  ) : (
                    <CalendarVcardV2 data={previewData} isPreview />
                  )}
                </div>
              </>
            ) : (
              <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, color: "#9ca3af", padding: "2rem", textAlign: "center" }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#fff0f4", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>🎨</div>
                <div>
                  <p style={{ fontSize: 15, fontWeight: 600, color: "#4b5563", margin: "0 0 6px" }}>Preview not available yet</p>
                  <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>Please fill in your profile details to see the preview.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ─── exported component ─── */
function ColorPickerV2({
  pageData,
  themeVal,
  primary = "#a00030",
  secondary = "#B7B7B7",
  heading = "#000000",
  subHeading = "#EEEEEE",
  onChange,
  onOpen,
}) {
  const [open, setOpen] = useState(false);

  const [selectedPalette, setSelectedPalette] = useState({
    primary, secondary, heading, subHeading
  });

  useEffect(() => {
    setSelectedPalette({ primary, secondary, heading, subHeading });
  }, [primary, secondary, heading, subHeading]);

  const handleSelectColor = (palette) => {
    setSelectedPalette(palette);
    if (onChange) {
      onChange(palette);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => {
          // Just open + refresh preview data from current committed state.
          // Do NOT also call handleSelectColor here — it duplicates onChange
          // and races with onOpen's stale closure, overwriting a correct
          // preview with a stale one.
          if (onOpen) onOpen();
          setOpen(true);
        }}
        style={{
          width: "100%", height: 52, borderRadius: 12, overflow: "hidden",
          display: "flex", border: "2px solid #e5e7eb", cursor: "pointer",
          padding: 0, transition: "all 0.2s", background: "none",
        }}
      >
        <div style={{ background: primary, width: "50%", height: "100%" }} />
        <div style={{ background: secondary, width: "25%", height: "100%" }} />
        <div style={{ background: heading, width: "13%", height: "100%" }} />
        <div style={{ background: subHeading, width: "12%", height: "100%" }} />
      </button>

      <p style={{ fontSize: 12, color: "#9ca3af", marginTop: 6 }}>
        Click the bar above to choose a colour theme
      </p>

      <ColorPickerModal
        pageData={pageData}
        themeVal={themeVal}
        open={open}
        onClose={() => setOpen(false)}
        onSelectColor={handleSelectColor}
        onNeedPreviewData={onOpen}
        selectedPalette={selectedPalette}
        currentProps={{ primary, secondary, heading, subHeading }}
      />
    </div>
  );
}

export default ColorPickerV2;